import type { Attachment, AttachmentKindName, PresignedUpload } from "@dentacore/shared-types";
import { ALLOWED_MIME_TYPES, MAX_UPLOAD_BYTES } from "@dentacore/shared-types";

import { confirmAttachment, presignAttachment } from "../api/patients";

/**
 * The longest edge of a generated thumbnail, in pixels.
 *
 * 320 covers the table's preview at two-times device scale with nothing to
 * spare — a thumbnail is a thumbnail, and the full image is one signed URL
 * away when someone opens the viewer.
 */
const THUMBNAIL_MAX_EDGE = 320;

export class UploadRejected extends Error {}

/**
 * Put one file on a patient's file, without it ever touching the API server.
 *
 * ── The shape, and why it is four steps rather than one ─────────────────────
 *
 *   1. the browser hashes the bytes and draws a thumbnail
 *   2. the API signs one URL per object
 *   3. the browser `PUT`s both straight to storage
 *   4. the API is told it worked
 *
 * Step 3 is the point (P2 Q5). A radiograph never passes through the API, so
 * the API never has to be sized for radiographs — and the checksum in step 1
 * is what makes a corrupted transfer detectable at all, since nothing on the
 * server ever sees the bytes to check them.
 *
 * **The hash proves transfer integrity, not authenticity.** This code asserts
 * its own file's hash, so a corrupted upload is caught and a lying client is
 * not. That was the accepted cost of not putting the bytes back through the
 * API; it is written down here as well as in DECISIONS.md because this is the
 * file where someone would otherwise assume it means more than it does.
 *
 * The client-side checks below are a **convenience, not a control** — the
 * presign request enforces the same allowlist, because a browser that skipped
 * these would otherwise be handed a signed URL for anything at all.
 */
export async function uploadAttachment(
  patientId: string,
  file: File,
  meta: { kind: AttachmentKindName; region?: string; appointmentId?: string },
): Promise<Attachment> {
  if (!(ALLOWED_MIME_TYPES as readonly string[]).includes(file.type)) {
    throw new UploadRejected(`${file.name} is not a JPEG, PNG or PDF.`);
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new UploadRejected(`${file.name} is over the 25 MB limit.`);
  }
  if (file.size === 0) {
    throw new UploadRejected(`${file.name} is empty.`);
  }

  const bytes = await file.arrayBuffer();
  const sha256 = await hashHex(bytes);
  // A PDF cannot be drawn to a canvas, so it goes up without a preview and the
  // table shows a file icon instead (P2 Q6).
  const thumbnail = file.type === "application/pdf" ? null : await drawThumbnail(file);

  const signed: PresignedUpload = await presignAttachment(patientId, {
    mimeType: file.type,
    sizeBytes: file.size,
    withThumbnail: thumbnail !== null,
  });

  await put(signed.uploadUrl, bytes, file.type);

  if (thumbnail !== null && signed.thumbnailUploadUrl !== null) {
    await put(signed.thumbnailUploadUrl, thumbnail, "image/png");
  }

  return confirmAttachment(patientId, {
    s3Key: signed.s3Key,
    thumbnailS3Key: signed.thumbnailS3Key ?? undefined,
    mimeType: file.type,
    sizeBytes: file.size,
    sha256,
    kind: meta.kind,
    region: meta.region,
    appointmentId: meta.appointmentId,
  });
}

/**
 * `PUT` straight to storage.
 *
 * **No credentials and no `Authorization` header** — the signature is in the
 * URL's query string, and sending our own bearer token to a third-party host
 * would leak it. `fetch` rather than the API client for exactly that reason:
 * the API client attaches the token to everything it sends.
 */
async function put(url: string, body: ArrayBuffer | Blob, contentType: string): Promise<void> {
  const response = await fetch(url, {
    method: "PUT",
    body,
    headers: { "content-type": contentType },
  });

  if (!response.ok) {
    throw new UploadRejected(
      `Storage refused the upload (${String(response.status)}). The link may have expired — try again.`,
    );
  }
}

/** Lower-case hex SHA-256, which is what the API's validator expects. */
async function hashHex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Draw the image down to a preview, preserving its aspect ratio.
 *
 * `createImageBitmap` rather than an `<img>` and a load event: it decodes off
 * the main thread and does not need the element to be in the document, so a
 * batch of radiographs does not lock the interface while it processes.
 *
 * Returns null rather than throwing when decoding fails — a file the browser
 * cannot render is still worth storing, and losing the upload to protect the
 * preview would be the wrong trade on a clinical record.
 */
async function drawThumbnail(file: File): Promise<Blob | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, THUMBNAIL_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (context === null) return null;
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/png");
    });
  } catch {
    return null;
  }
}
